import {
  ELProduct,
  FixedPrices,
  HGLELProduct,
  HGLNGProduct,
  MeterType,
  MeterUsage,
  NGProduct,
  SimulatePriceResponse,
  SimulationSteps
} from 'features/simulation/types'
import { CountryCode, parsePhoneNumberFromString } from 'libphonenumber-js/max'
import { MarketingStore, SimulationType } from 'store/marketing/types'
import { SimulationInputs, SimulationStore } from 'store/simulation/types'
import { PricelistTypes } from 'types/product-data'
import { ContactData, Subscribe } from 'types/subscribe'
import { Language } from 'types/language'
import { store } from 'store'
import { checkPromoReferralCode } from 'api/customerFlow'
import { HERO_IMAGES } from 'features/home/constants'
import { toggleContactMe } from 'store/simulation/actions/toggleContactMe'
import { goToSimulationStep } from 'store/simulation/actions/switchSimulationStep'
import { showSimulation } from 'store/simulation/actions/showSimulation'
import { getPriceSimulation, storeSimulationParams } from 'features/simulation/utils'
import { SimulationInputKeys } from 'store/simulation/types'
import { CheckPromoReferralCodeResponse, CodeTypes } from 'types/promo-referral-codes'
import { getPriceSimulationAfterPromoCodeValidation } from 'features/simulation/utils'
import { updateSimulationInput } from 'store/simulation/actions/updateSimulationInput'

/**
 * Temporary function to set hard-coded VIAVIA promocode
 * Used with cashback referral campaign
 *
 * @param onError
 */
export const addCashbackPromoCodeToStore = async (onError?: () => void) => {
  try {
    const checkedPromoCode = await checkPromoReferralCode('VIAVIA')

    if (checkedPromoCode.valid) {
      const { promoCode } = checkedPromoCode

      store.dispatch(
        updateSimulationInput({
          key: SimulationInputKeys.PROMO_CODE,
          value: promoCode
        })
      )
    }
  } catch (e) {
    if (onError) {
      onError()
    } else {
      throw new Error(e)
    }
  }
}

/**
 * Adds the checked promo or referral code to store (if valid)
 *
 * @param checkedPromoReferralCode
 * @param onError
 */
export const addCheckedPromoReferralCodeToStore = async (checkedPromoReferralCode: CheckPromoReferralCodeResponse, onError: () => void) => {
  if (checkedPromoReferralCode.valid) {
    if (checkedPromoReferralCode.type === CodeTypes.REFERRAL) {
      // If checked code is referralcode
      store.dispatch(updateSimulationInput({ key: SimulationInputKeys.REFERRAL_CODE, value: checkedPromoReferralCode.referralCode }))

      // !! We also need to set the cashback promocode (= VIAVIA) because of the referral campaign !!
      await addCashbackPromoCodeToStore(onError)
    } else {
      // If checked code is promocode
      store.dispatch(updateSimulationInput({ key: SimulationInputKeys.PROMO_CODE, value: checkedPromoReferralCode.promoCode }))
    }

    /**
     * Re-simulate
     * !! Normally only re-simulate if code is promo code, but as the cashback referral campaigns contains both
     * promocode AND referralcode, always re-simulate !!
     */
    getPriceSimulationAfterPromoCodeValidation()
  }
}

/**
 * Calculates the distances between 2 lat-lng points
 *
 * @param lat1
 * @param lon1
 * @param lat2
 * @param lon2
 * @returns number
 */
export const calcDistance = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
  const radlat1 = (Math.PI * lat1) / 180
  const radlat2 = (Math.PI * lat2) / 180
  const theta = lon1 - lon2
  const radtheta = (Math.PI * theta) / 180

  let dist = Math.sin(radlat1) * Math.sin(radlat2) + Math.cos(radlat1) * Math.cos(radlat2) * Math.cos(radtheta)

  if (dist > 1) {
    dist = 1
  }

  dist = Math.acos(dist)
  dist = (dist * 180) / Math.PI
  dist = dist * 60 * 1.1515

  // Calculate distance in KM's
  return dist * 1.609344
}

/**
 * Creates a URI encoded data string from the given data
 *
 * @param data
 * @returns string
 */
export const createURIEncodedDataString = (data: Record<string, string>): string => {
  const formBody: string[] = []

  Object.keys(data).map((key) => {
    formBody.push(encodeURIComponent(key) + '=' + encodeURIComponent(data[key]))
  })

  return formBody.join('&')
}

/**
 * Convertes a file to base64
 *
 * @param {File} file
 * @returns {Promise<string|ArrayBuffer>}
 */
export const fileToBase64 = (file: File): Promise<string | ArrayBuffer> =>
  new Promise((resolve) => {
    const reader = new FileReader()
    reader.readAsDataURL(file)
    reader.onload = () => resolve(reader.result)
  })

/**
 * Provides SEO image through Cloudinary
 */
export const getCloudinarySEOImage = (imageUrl: string): string => {
  const prefix = 'https://res.cloudinary.com/bolt-energie/image/fetch/w_1200,h_630/f_auto/'

  return `${prefix}${imageUrl}`
}

/**
 * Returns the fixed prices for API requests
 *
 * @param {FixedPrices} fixedPrices
 * @param {SimulationType} simulationType
 * @returns {FixedPrices}
 */
export const getFixedPricesForApiRequests = (fixedPrices: FixedPrices, simulationType: SimulationType): FixedPrices => {
  if (simulationType !== SimulationType.PRICING_SIMULATION) {
    if (fixedPrices) return fixedPrices

    return {
      electricity: 0,
      gas: 0
    }
  }

  return undefined
}

/**
 * Finds correct imported image according to filename
 *
 * @param heroImage
 * @returns StaticImageData
 */
export const getHeroImage = (heroImage: string): StaticImageData => {
  return HERO_IMAGES.filter((heroImageObj) => heroImageObj.fileName === heroImage)[0].image
}

/**
 * Returns the pricelist for a given electricity product
 *
 * @param products
 * @param product
 * @param isCompany
 * @param language
 * @returns
 */
export const getPricelistFromElectricityProducts = (product: ELProduct, isCompany: boolean, language: Language): string => {
  if (store) {
    const { products } = store.getState().app

    const hglElProduct = HGLELProduct[product]
    const pricelistType = isCompany ? PricelistTypes.PROFESSIONAL : PricelistTypes.RESIDENTIAL

    return products ? products.electricity[hglElProduct].pricelists[pricelistType][language] : ''
  }

  return ''
}

/**
 * Returns the pricelist for a given gas product
 *
 * @param products
 * @param product
 * @param isCompany
 * @param language
 * @returns
 */
export const getPricelistFromGasProducts = (product: NGProduct, isCompany: boolean, language: Language): string => {
  if (store) {
    const { products } = store.getState().app

    const hglNgProduct = HGLNGProduct[product]
    const pricelistType = isCompany ? PricelistTypes.PROFESSIONAL : PricelistTypes.RESIDENTIAL

    return products ? products.gas[hglNgProduct].pricelists[pricelistType][language] : ''
  }

  return ''
}

/**
 * Stores the given serialized simulation inputs & fetches a simulation with them
 *
 * @param string serializedInputs
 * @returns Promise<void>
 */
export const getPriceSimulationWithInputs = async (serializedInputs: string): Promise<void> => {
  await storeSimulationParams(serializedInputs)
  getPriceSimulation()
}

/**
 * Returns the total volume based on the meter type
 *
 * @param meter
 * @param usage
 */
export const getTotalElectricityVolumeForMeterType = (meter: MeterType, usage: MeterUsage): number => {
  if (meter === MeterType.SINGLE_RATE) return Number(usage.singleRate + usage.exclNight)

  return Number(usage.doubleRate.day + usage.doubleRate.night + usage.exclNight)
}

/**
 * Returns the total price
 * @param price
 * @param inputs
 * @param didSimulation
 * @returns number|string
 */
export const getTotalPrice = (price: SimulatePriceResponse, inputs: SimulationInputs, didSimulation?: boolean): number | string => {
  if (didSimulation && price) {
    return inputs.hasVat ? price.total.totalWithoutVAT : price.total.total
  }

  return '-,-'
}

/**
 * Handles the contact me button click (for noOptinOne flow)
 */
export const handleContactMeClick = (): void => {
  store.dispatch(toggleContactMe())
  store.dispatch(goToSimulationStep({ step: SimulationSteps.SUBSCRIBE }))
  store.dispatch(showSimulation())
}

/**
 * Scrolls to top of window
 */
export const handleScrollToTop = (): void => {
  window?.scrollTo({ top: 0, behavior: 'smooth' })
}

export const hasNoNumber = (text: string): boolean => {
  return !/\d/.test(text)
}

/**
 * Returns if the given barcode string is a valid EAN number
 *
 * @param barcode
 * @returns
 */
export const isValidEAN = (barcode: string): boolean => {
  // Check if EAN is 18 characters long
  if (barcode.length !== 18) return false

  // Check if EAN is a number
  if (isNaN(parseInt(barcode, 10))) return false

  // Check if the last digit is a number
  const lastDigit = parseInt(barcode.substring(barcode.length - 1), 10)
  if (isNaN(lastDigit)) return false

  // Reverse the all the numbers except the last
  const arr = barcode
    .substring(0, barcode.length - 1)
    .split('')
    .reverse()

  // Calculate the sum of all the numbers
  // Numbers on even positions are multiplied by 3 and then added
  // Numbers on odd positions are added without being multiplied
  let sum = 0
  for (let i = 0; i < arr.length; i++) {
    if (i % 2 === 0) {
      sum += parseInt(arr[i], 10) * 3
    } else {
      sum += parseInt(arr[i], 10)
    }
  }

  // Calculate the checksum
  let checkSum = sum % 10

  // Check & return if the checksum equals the last digit if the checksum equals 0
  if (checkSum === 0) return checkSum === lastDigit

  // Recalculate the checksum & check if last digit matches if checksum didn't equal 0
  checkSum = 10 - (sum % 10)

  return checkSum === lastDigit
}

/**
 * Returns if a given postal code is valid
 *
 * @param postalCode
 * @returns boolean
 */
export const isValidPostalCode = (postalCode: number): boolean => {
  return postalCode.toString().length === 4
}

/**
 * Parse contactData, simulation store & marketing state as a Subscribe typed object to subscribe the lead through the API
 *
 * @param contactData
 * @param simulation
 * @param marketing
 * @param startedFulfillmentFlow
 */
export const parseSubscribeData = (
  contactData: ContactData,
  simulation: SimulationStore,
  marketing: MarketingStore,
  phoneCountryCode?: CountryCode,
  startedFulfillmentFlow?: boolean,
  pardotFormHandler?: string
): Subscribe => {
  // Destructure data from given params
  const { inputs, fixedPrices } = simulation
  const { companyName, firstName, lastName, email, mobilePhone, language, opportunityId } = contactData

  return {
    opportunityId: opportunityId || '',
    firstName,
    lastName,
    email,
    language,
    mobilePhone: (mobilePhone && parsePhoneNumberFromString(mobilePhone, phoneCountryCode ?? 'BE')?.formatInternational()) || '',
    companyName: companyName || '',
    simulation: {
      isCompany: inputs.hasVat || false,
      knowsUsage: inputs.knowsUsage || false,
      exclNight: inputs.hasExclusiveNightMeter || false,
      needsGas: inputs.needsGas || false,
      hasSolarPanels: inputs.hasSolarPanels || false,
      solarKv: inputs.solarPower || 0,
      counterType: inputs.counterType || undefined,
      product: inputs.haugazelProducts,
      meter: inputs.meterType,
      meterUsage: inputs.usage,
      price: {
        electricity:
          (simulation.price && (inputs.hasVat ? simulation.price.electricity.totalWithoutVAT : simulation.price.electricity.total)) || 0,
        gas:
          (simulation.price &&
            simulation.price.gas &&
            (inputs.hasVat ? simulation.price.gas.totalWithoutVAT : simulation.price.gas.total)) ||
          0
      },
      fixedPrices: getFixedPricesForApiRequests(fixedPrices, marketing.simulationType),
      usageType: inputs.usageType,
      deliveryPoint: inputs.deliveryPoint,
      ...(inputs.promoCode && {
        discountCode: inputs.promoCode
      }),
      ...(inputs.referralCode && {
        referralCode: inputs.referralCode
      })
    },
    simulationParams: inputs,
    startedFulfillmentFlow: startedFulfillmentFlow || false,
    electricityProduct: inputs.haugazelProducts.electricity,
    gasProduct: inputs.haugazelProducts.gas,
    pardotFormHandler: pardotFormHandler ?? undefined,
    confirmationRoundup: marketing.confirmationRoundup,

    // Marketing params
    agentID: marketing.agentID ?? undefined,
    campaignName: marketing.campaignName ?? undefined,
    emailConfirmationFlow: marketing.emailConfirmationFlow,
    eventID: marketing.eventID ?? undefined,
    eventName: marketing.eventName ?? undefined,
    gclid: marketing.gclid ?? undefined,
    leadSource: marketing.leadSource ?? undefined,
    nurtureFlow: marketing.nurtureFlow ?? undefined,
    partnerID: marketing.partnerID ?? undefined,
    producerVisual: marketing.producerVisual,
    shiftID: marketing.shiftID ?? undefined,
    signChannel: marketing.signChannel,
    simulationType: marketing.simulationType,
    simulationSo: marketing.simulationSo ?? undefined,
    sourceType: marketing.sourceType ?? undefined
  }
}

/**
 * Removes spaces and dots in the given string & returns it
 *
 * @param {string} text
 * @returns {string}
 */
export const removeSpacesAndDots = (text: string): string => {
  const noSpaces = text.replace(/ /g, '')
  const noDots = noSpaces.replace(/\./g, '')

  return noDots
}

/**
 * Shuffles any given array with the Fisher-Yates algorithm
 *
 * @param array
 * @returns any[]
 */
export const shuffleArray = (array: any[]): any[] => {
  for (let i = array.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    const temp = array[i]

    array[i] = array[j]
    array[j] = temp
  }

  return array
}
